<template>
  <div class="vtm-table">
    <!-- 单行控制条：操作 · 即时搜索 · 结构化筛选 · 列设置 -->
    <div class="vtm-table__toolbar">
      <div v-if="$slots['toolbar-left']" class="vtm-table__group">
        <slot name="toolbar-left"></slot>
      </div>

      <el-input
        v-if="quickSearch"
        v-model="quickTerm"
        size="small"
        clearable
        class="vtm-table__search"
        prefix-icon="el-icon-search"
        :placeholder="searchPlaceholder || $t('common.quickSearch')"
      ></el-input>

      <div v-if="$slots['toolbar-filters']" class="vtm-table__group vtm-table__filters">
        <slot name="toolbar-filters"></slot>
      </div>

      <span v-if="quickTerm" class="vtm-table__count">{{ matchCountText }}</span>

      <div class="vtm-table__group vtm-table__right">
        <slot name="toolbar-right"></slot>
        <el-popover placement="bottom-end" width="260" trigger="click" popper-class="vtm-table__cols-popper">
          <div class="vtm-cols">
            <div class="vtm-cols__head">
              <span class="vtm-cols__title">{{ $t("common.columnSettings") }}</span>
              <el-button type="text" size="mini" @click="resetColumns">{{ $t("common.resetColumns") }}</el-button>
            </div>
            <div class="vtm-cols__list">
              <div
                v-for="(col, index) in colState"
                :key="col.prop"
                class="vtm-cols__row"
                :class="{ 'is-dragover': dragOverIndex === index }"
                draggable="true"
                @dragstart="onDragStart(index, $event)"
                @dragover.prevent="dragOverIndex = index"
                @dragleave="dragOverIndex = -1"
                @drop.prevent="onDrop(index)"
                @dragend="dragOverIndex = -1"
              >
                <i class="el-icon-rank vtm-cols__grip" :title="$t('common.dragToReorder')"></i>
                <el-checkbox
                  :value="col.visible"
                  :disabled="col.visible && visibleCount === 1"
                  @change="toggleColumn(index)"
                >
                  {{ labelOf(col.prop) }}
                </el-checkbox>
                <span class="vtm-cols__arrows">
                  <button
                    type="button"
                    class="vtm-cols__arrow"
                    :disabled="index === 0"
                    :aria-label="$t('common.moveUp')"
                    @click="moveColumn(index, -1)"
                  >
                    <i class="el-icon-arrow-up"></i>
                  </button>
                  <button
                    type="button"
                    class="vtm-cols__arrow"
                    :disabled="index === colState.length - 1"
                    :aria-label="$t('common.moveDown')"
                    @click="moveColumn(index, 1)"
                  >
                    <i class="el-icon-arrow-down"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
          <el-button slot="reference" size="small" icon="el-icon-s-operation" class="vtm-table__cols-btn">
            {{ $t("common.columns") }}
          </el-button>
        </el-popover>
      </div>
    </div>

    <el-table :data="displayData" v-loading="loading" style="width: 100%" v-bind="$attrs" v-on="$listeners">
      <!-- 前置列（多选/序号等） -->
      <slot name="prepend"></slot>

      <template v-for="col in visibleColumns">
        <!-- 有自定义插槽的列：走插槽渲染 -->
        <el-table-column
          v-if="$scopedSlots[col.prop]"
          :key="col.prop + '::slot'"
          :prop="col.prop"
          :label="labelOf(col.prop)"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align || 'left'"
          :sortable="col.sortable"
          :formatter="col.formatter"
          :show-overflow-tooltip="col.tooltip !== false"
        >
          <template slot-scope="scope">
            <slot :name="col.prop" v-bind="scope"></slot>
          </template>
        </el-table-column>
        <!-- 无插槽的列：保留 Element 原生单元格渲染（row[prop] / formatter），
             否则条件插槽会吞掉默认渲染导致整列空白 -->
        <el-table-column
          v-else
          :key="col.prop"
          :prop="col.prop"
          :label="labelOf(col.prop)"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align || 'left'"
          :sortable="col.sortable"
          :formatter="col.formatter"
          :show-overflow-tooltip="col.tooltip !== false"
        ></el-table-column>
      </template>

      <!-- 后置列（操作列 fixed right 等） -->
      <slot name="append"></slot>
    </el-table>
  </div>
</template>

<script>
  // 列可管理 + 即时搜索的数据表（单行控制条）。
  //   - quickSearch：跨可见列的前端即时过滤（无需按钮），可用 search-fields 限定字段
  //   - toolbar-left / toolbar-filters / toolbar-right：插槽，把操作与结构化筛选并入同一行
  //   - 列显隐 / 拖拽或箭头排序 / 按用户持久化（uid + tableId）
  // 用法参考：src/views/order/Order.vue
  const STORAGE_PREFIX = "vtm-cols";

  export default {
    name: "VtmTable",
    inheritAttrs: false,
    props: {
      tableId: { type: String, required: true },
      columns: { type: Array, required: true },
      data: { type: Array, default: () => [] },
      loading: { type: Boolean, default: false },
      quickSearch: { type: Boolean, default: true },
      searchPlaceholder: { type: String, default: "" },
      // 限定即时搜索的字段（列 prop 数组）；不传则搜索全部可见列
      searchFields: { type: Array, default: null }
    },
    data() {
      return {
        colState: [],
        dragIndex: -1,
        dragOverIndex: -1,
        quickTerm: ""
      };
    },
    computed: {
      storageKey() {
        const uid = (this.$store && this.$store.state.uid) || "anon";
        return `${STORAGE_PREFIX}:${uid}:${this.tableId}`;
      },
      columnMap() {
        const map = {};
        this.columns.forEach(col => {
          map[col.prop] = col;
        });
        return map;
      },
      visibleColumns() {
        return this.colState.filter(col => col.visible && this.columnMap[col.prop]).map(col => this.columnMap[col.prop]);
      },
      visibleCount() {
        return this.colState.filter(col => col.visible).length;
      },
      // 即时搜索：跨字段的前端过滤（对已加载行，大小写不敏感）
      displayData() {
        const term = this.quickTerm.trim().toLowerCase();
        if (!term) {
          return this.data;
        }
        const fields = this.searchFields && this.searchFields.length
          ? this.searchFields
          : this.visibleColumns.map(col => col.prop);
        return this.data.filter(row => {
          return fields.some(prop => {
            const col = this.columnMap[prop];
            let raw = this.getByPath(row, prop);
            // 有 formatter 的列，用格式化后的展示值一并匹配
            if (col && typeof col.formatter === "function") {
              try {
                raw = col.formatter(row, col, raw);
              } catch (e) {
                /* 忽略格式化异常，回退原值 */
              }
            }
            if (raw === null || raw === undefined) {
              return false;
            }
            return String(raw).toLowerCase().includes(term);
          });
        });
      },
      matchCountText() {
        return this.$t("common.matchCount", { n: this.displayData.length, total: this.data.length });
      }
    },
    watch: {
      columns: {
        immediate: true,
        handler() {
          this.initState();
        }
      }
    },
    methods: {
      getByPath(row, path) {
        if (!path || row == null) {
          return undefined;
        }
        if (path.indexOf(".") === -1) {
          return row[path];
        }
        return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), row);
      },
      labelOf(prop) {
        const col = this.columnMap[prop];
        return col ? col.label : prop;
      },
      defaultState() {
        return this.columns.map(col => ({ prop: col.prop, visible: col.hidden !== true }));
      },
      initState() {
        const fallback = this.defaultState();
        let saved = null;
        try {
          saved = JSON.parse(localStorage.getItem(this.storageKey));
        } catch (e) {
          saved = null;
        }
        if (!Array.isArray(saved) || !saved.length) {
          this.colState = fallback;
          return;
        }
        const known = new Set();
        const merged = [];
        saved.forEach(item => {
          if (item && this.columnMap[item.prop] && !known.has(item.prop)) {
            known.add(item.prop);
            merged.push({ prop: item.prop, visible: item.visible !== false });
          }
        });
        fallback.forEach(item => {
          if (!known.has(item.prop)) {
            merged.push(item);
          }
        });
        this.colState = merged;
      },
      persist() {
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(this.colState));
        } catch (e) {
          /* ignore */
        }
      },
      toggleColumn(index) {
        const col = this.colState[index];
        if (col.visible && this.visibleCount === 1) {
          return;
        }
        this.$set(this.colState, index, { ...col, visible: !col.visible });
        this.persist();
      },
      moveColumn(index, delta) {
        const target = index + delta;
        if (target < 0 || target >= this.colState.length) {
          return;
        }
        const next = [...this.colState];
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        this.colState = next;
        this.persist();
      },
      onDragStart(index, event) {
        this.dragIndex = index;
        event.dataTransfer.effectAllowed = "move";
        try {
          event.dataTransfer.setData("text/plain", String(index));
        } catch (e) {
          /* IE */
        }
      },
      onDrop(index) {
        if (this.dragIndex < 0 || this.dragIndex === index) {
          this.dragIndex = -1;
          return;
        }
        const next = [...this.colState];
        const [moved] = next.splice(this.dragIndex, 1);
        next.splice(index, 0, moved);
        this.colState = next;
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        this.persist();
      },
      resetColumns() {
        this.colState = this.defaultState();
        try {
          localStorage.removeItem(this.storageKey);
        } catch (e) {
          /* ignore */
        }
      }
    }
  };
</script>

<style scoped lang="scss">
  .vtm-table__toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 0 12px;
  }

  .vtm-table__group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .vtm-table__search {
    width: 240px;
    flex: 0 1 240px;
  }

  .vtm-table__filters {
    flex: 1 1 auto;
  }

  .vtm-table__count {
    font-size: 12px;
    color: var(--vtm-ink-3);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .vtm-table__right {
    margin-left: auto;
  }

  .vtm-cols__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    margin-bottom: 6px;
    border-bottom: 1px solid var(--vtm-line);
  }

  .vtm-cols__title {
    font-family: var(--vtm-display);
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .vtm-cols__list {
    max-height: 320px;
    overflow-y: auto;
  }

  .vtm-cols__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 4px;
    border-radius: var(--vtm-r-sm);
    transition: background-color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
    }

    &.is-dragover {
      background: var(--vtm-mint-soft);
    }
  }

  .vtm-cols__grip {
    color: var(--vtm-ink-3);
    cursor: grab;
    font-size: 13px;
  }

  .vtm-cols__row ::v-deep .el-checkbox {
    flex: 1;
    min-width: 0;

    .el-checkbox__label {
      font-size: 12.5px;
      color: var(--vtm-ink);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 150px;
      vertical-align: middle;
    }
  }

  .vtm-cols__arrows {
    display: inline-flex;
    gap: 2px;
  }

  .vtm-cols__arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--vtm-ink-3);
    cursor: pointer;

    &:hover:not(:disabled) {
      background: var(--vtm-mint-soft);
      color: var(--vtm-mint-ink);
    }

    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
</style>
